import { useEffect, useState } from 'react';
import { inject } from 'mobx-react';
import { Table, Row, Col } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import { SimpleSelectWidget } from '../../../components/GenericForm/Widgets/SimpleSelect.js';

//elements
import CustomMultiSelect from '../../../elements/CustomMultiSelect';
import RequiredStar from '../../../elements/RequiredStar.js';
import LoadingSpinner from '../../../elements/LoadingSpinner.js';

//assets
import Edit from '../../../assets/img/editing.svg';
import AddressBookIcon from '../../../assets/img/AddressBookIcon.svg';
import Delete from '../../../assets/img/deleting.svg';

//styles
import './styles.css';

const renderAssigneeInGroup = (userList, t) => {
    if (!userList || !userList.assigned_users || !userList.assigned_users.length) return (
        <span className='font-size-12 fw-bold'>{t("No employee assigned, please add employees to group")}</span>
    )
    return (
        <div className='group-management-assignee_list'>
            {userList.assigned_users.map((ele, idx) => {
                return <span key={uuid()}>{ele.label}{idx !== userList.assigned_users.length - 1 ? ";" : ""}</span>
            })}
        </div>
    )
}

const checkIfGroupInUse = (group) => {
    if (!group || ((!group.assigned_users || !group.assigned_users.length) && !group.use_count)) return false;
    return true;
}

const ManageGroupsWidget = (props) => {
    const { t, userStore } = props;
    const { pmLists } = userStore;
    const currentUser = userStore.currentUser;
    const isAdmin = currentUser.user_type === 'admin';

    const [loading, setLoadingStatus] = useState(false);
    const [uniqueReloadId, setUniqueReloadId] = useState(uuid());
    const [userGroupByUserAndUsageDetails, setUserGroupByUserAndUsageDetails] = useState({});
    const [userOptions, setUserOptions] = useState([]);
    const [editing, setEditing] = useState(0);
    const [isSubmitBtnClicked, setSubmitBtnStatus] = useState(false);
    const [currentEditing, setCurrentEditing] = useState(
        {
            code: "",
            name: "",
            assigned_users: [],
            assigned_pm: null,
            created_by: null,
            allow_code_update: true,
            allow_name_update: true
        });

    useEffect(() => {
        setLoadingStatus(true);
        loadData();
    }, []);

    const loadData = async (resetForm = false) => {
        try {
            const data = await props.userGroupStore.getGroupByUserAndUsageDetails();
            const users = await userStore.loadLookup('members', '');
            const parsedList = users.map(ele => { return { value: ele.id, label: ele.fullname, disabled: false, isDisabled: false } })
            setUserGroupByUserAndUsageDetails(data);
            setUserOptions(parsedList);
            setLoadingStatus(false);
            if (resetForm) {
                setSubmitBtnStatus(false);
                setEditing(0);
                setCurrentEditing({ code: "", name: '', assigned_users: [], allow_code_update: true, allow_name_update: true });
            }
        }
        catch (err) {
            setLoadingStatus(false);
            console.error(err)
        }

    }

    const handleChange = (name, value) => {
        if (name === "code") {
            const pattern = /^[0-9]+$/;
            if (!pattern.test(value)) return;
        }
        setCurrentEditing({
            ...currentEditing,
            [name]: value
        })
    }

    const handleSubmit = () => {
        if (!currentEditing.code)
            return props.commonStore.addNotification(t('Please enter code'), null, 'warning');

        if (!currentEditing.name || !currentEditing.name.trim())
            return props.commonStore.addNotification(t('Please enter name'), null, 'warning');

        if (!currentEditing.assigned_pm)
            return saveGroupChanges();
        const currentSelectedPmMembers = pmLists.find(pm => pm.value === currentEditing.assigned_pm)?.members || [];
        const nonPmMembers = currentEditing.assigned_users.filter(userId => !currentSelectedPmMembers.includes(userId));
        if (nonPmMembers.length)
            autoUpdatePmMembersAndSaveChanges(currentEditing.assigned_pm, currentSelectedPmMembers, nonPmMembers)
        else saveGroupChanges();
    }

    const autoUpdatePmMembersAndSaveChanges = (pmId, pmMemberList, nonMemberList) => {
        const updatePmMemberList = { ManagesUsers: [...pmMemberList, ...nonMemberList] };
        const message = t("Note") + ": " + t('The selected members are not assigned to this PM. They will be assigned automatically, do you want continue') + " ?";
        return confirmAlert({
            title: t('Auto-assign members to PM?'),
            message,
            buttons: [
                {
                    label: t('Auto-assign And Save'),
                    onClick: () => {
                        setSubmitBtnStatus(true);
                        userStore.assignMembersToPm(pmId, updatePmMemberList).then(saveGroupChanges).catch(error => {
                            props.commonStore.addNotification(t(error.message || 'Something went wrong'), null, 'error');
                            setSubmitBtnStatus(false);
                            console.error(error)
                        });

                    },
                },
                {
                    label: t('Cancel and discard changes'),
                    onClick: () => handleGroupActions('cancel', null, 0)
                },
            ],
        });
    }

    const saveGroupChanges = () => {
        if (!isSubmitBtnClicked)
            setSubmitBtnStatus(true);
        addUpdatedGroupDetailsAndAssignees();
    }

    const addUpdatedGroupDetailsAndAssignees = async () => {
        try {
            await props.userGroupStore.addOrUpdateUserGroupAndAssignees({
                code: currentEditing.code,
                name: currentEditing.name,
                users: currentEditing.assigned_users,
                assigned_pm: currentEditing.assigned_pm,
                created_by: currentEditing.created_by,
            }, editing === 0).then(res => {
                props.commonStore.addNotification(t(res.message), null, 'success');
                loadData(true);
            }).catch(err => {
                props.commonStore.addNotification(err.message, null, 'error');
                setSubmitBtnStatus(false);
            });
        } catch (err) {
            console.error(err)
        }
    }

    const removeGroup = async (code) => {
        try {
            await props.userGroupStore.removeUserGroup(code).then(res => {
                props.commonStore.addNotification(t(res.message), null, 'success');
                loadData(true);
            }).catch(err => {
                props.commonStore.addNotification(err.message, null, 'error');
                setLoadingStatus(false);
            });
        } catch (err) {
            console.error(err)
        }
    }

    const handleGroupActions = (type, data, index) => {
        switch (type) {
            case "assign":
            case "edit":
                setEditing(index);
                setCurrentEditing({
                    code: data.code,
                    name: data.name,
                    assigned_users: data.assigned_users.map(ele => ele.value),
                    assigned_pm: data.assigned_pm,
                    allow_code_update: !checkIfGroupInUse(data),
                    allow_name_update: type === "edit",
                });
                const dataHasMap = data.assigned_users.reduce((a, c) => ({ ...a, [c.value]: c.inUse }), {});
                userOptions.forEach(ele => ele.disabled = !!dataHasMap[ele.value]);
                setUserOptions([...userOptions]);
                break;
            case "delete":
                setLoadingStatus(true);
                removeGroup(data.code);
                break;
            case "cancel":
                setEditing(index);
                setCurrentEditing({ code: "", name: '', assigned_users: [], allow_code_update: true, allow_name_update: true });
                setUserOptions([...userOptions.map(e => ({ ...e, disabled: false }))]);
                break;
            default:
                console.info("Default case");
        }
    }

    const handlePmAssignment = (selected) => {
        if (selected && !currentEditing?.assigned_users?.includes(selected)) {
            const message = t('The selected PM is not group member, so this PM will be automatically added to this group');
            setCurrentEditing({
                ...currentEditing,
                assigned_pm: selected,
                assigned_users: [...currentEditing.assigned_users, selected]
            });
            return props.commonStore.addNotification(message, null, 'info', 'top-right', 3000);
        } else
            handleChange("assigned_pm", selected)

    };

    const handleUserAssignment = (selected) => {
        if (currentEditing.assigned_pm && currentEditing?.assigned_users && !selected?.includes(currentEditing.assigned_pm)) {
            props.commonStore.addNotification(t('Since you have removed the PM from this group, the PM will no longer in the group. You can assign PM as group member if needed'), null, 'warning', 'top-right', 3000);
            setCurrentEditing({
                ...currentEditing,
                assigned_pm: null,
                assigned_users: selected
            });
            setUniqueReloadId(uuid());
        }
        else
            handleChange("assigned_users", selected);
    }

    return (
        <Row>
            <Row xs={12} className='group-management-header gx-0 align-items-center'>
                <Col xs={isAdmin ? 2 : 3} className='group-management-header-text'>
                    <input type={"number"} name={"Code"} placeholder={t('Code')} required
                        title={"Enter Unique Numeric Codes"}
                        value={currentEditing.code}
                        disabled={!currentEditing.allow_code_update}
                        onChange={evt => handleChange("code", evt.target.value)}
                    />
                    <RequiredStar />
                </Col>
                <Col xs={3} >
                    <input type={"text"} name={"Name"} placeholder={t('Enter Name')} required
                        value={currentEditing.name}
                        disabled={!currentEditing.allow_name_update}
                        onChange={evt => handleChange("name", evt.target.value)}
                    />
                    <RequiredStar />
                </Col>
                <Col xs={isAdmin ? 5 : 4} className='ps-1'>
                    {isAdmin && <SimpleSelectWidget
                        key={uniqueReloadId}
                        options={pmLists}
                        disabled={!currentEditing.allow_name_update}
                        placeholder={`${t('Select')} ${t('PM')}`}
                        value={pmLists.find(e => e.value === currentEditing.assigned_pm)?.value || null}
                        onChange={handlePmAssignment}
                        styleClasses={"custom-select-max-height-200"}
                        wrapperClass='w-50'
                    />}
                    <CustomMultiSelect
                        options={userOptions}
                        onChange={handleUserAssignment}
                        className={`${isAdmin ? 'w-50' : 'w-100'} custom-multi-select-max-height-200`}
                        value={currentEditing.assigned_users}
                        overrideStrings={{
                            selectSomeItems: t('Assign Employees'),
                            allItemsAreSelected: t('All Employees are selected'),
                            search: t('Search'),
                            selectAll: t('Select All'),
                        }}
                    />
                </Col>
                <Col xs={2}>
                    {currentEditing.code ?
                        <>
                            <Button fill me2 icon_sm title={t('Save')}
                                style={{ width: "35px", height: " 35px" }}
                                onClick={handleSubmit}>
                                <i className="fa fa-save fa-xl" />
                            </Button>
                            <Button icon_sm title={t('Cancel')}
                                style={{ width: "35px", height: " 35px" }}
                                onClick={() => handleGroupActions('cancel', null, 0)}>
                                <i className="fa fa-times fa-xl" />
                            </Button>
                        </>
                        :
                        <Button fill wd onClick={handleSubmit} disabled={isSubmitBtnClicked}>
                            <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Add new')}
                        </Button>
                    }
                </Col>
            </Row>
            < Table className='w-100' responsive hover>
                <thead>
                    <tr className='w-100 px-4 my-2'>
                        <th width={'5%'}>{t('#')}</th>
                        <th width={isAdmin ? '15%' : '20%'} className='text-start'>{t('Code')} | {t('Name')}</th>
                        {isAdmin && <th width={'15%'} className='text-start'>{t('Manager')}</th>}
                        <th width={isAdmin ? '35%' : '45%'} className='text-start'>{t('Assigned To')}</th>
                        <th width={'10%'} className='text-start'>{t('Shift Usage')}</th>
                        <th width={'20%'}>{t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ?
                        <tr>
                            <td colSpan={5}>
                                <LoadingSpinner />
                            </td>
                        </tr> :
                        Object.values(userGroupByUserAndUsageDetails).length ? Object.values(userGroupByUserAndUsageDetails).map((ele, idx) => (
                            <tr key={uuid()}
                                className='text-capitalize mt-1 px-4'
                                style={!editing ? {} : editing === (idx + 1) ? { border: '2px solid blue' } : { opacity: '0.6' }}>
                                <td width={'5%'} className='timelog_array_details'>{idx + 1}</td>
                                <td width={isAdmin ? '15%' : '20%'} className='timelog_array_details text-start'>{ele.code} | {ele.name}</td>
                                {isAdmin && <td width={'15%'} className='timelog_array_details text-start'>{ele.assigned_pm ? pmLists.find(e => e.value === ele.assigned_pm)?.label : '-'}</td>}
                                <td width={isAdmin ? '35%' : '40%'} className='timelog_array_details text-start'>{renderAssigneeInGroup(ele, t)}</td>
                                <td width={'10%'} className='timelog_array_details text-center'>{ele.use_count}</td>
                                <td width={'20%'} className='text-center'>
                                    <Button icon_sm fill me2
                                        title={t('Assign Members')}
                                        onClick={() => handleGroupActions('assign', ele, idx + 1)}
                                        disabled={!!editing}>
                                        <img src={AddressBookIcon} alt={'addressbook'} />
                                    </Button>

                                    <Button icon_sm fill me2
                                        title={t('Update Details')}
                                        onClick={() => handleGroupActions('edit', ele, idx + 1)}
                                        disabled={!!editing}>
                                        <img src={Edit} alt={'edit'} />
                                    </Button>

                                    <Button icon_sm_delete fill
                                        onClick={() => handleGroupActions('delete', ele)}
                                        disabled={currentUser.user_type !== 'admin' || checkIfGroupInUse(ele) || !!editing}
                                        title={currentUser.user_type !== 'admin' ? t('Only Admin can remove groups') : checkIfGroupInUse(ele) ? t('Group in use, can not be deleted') : t('Delete')}
                                    >
                                        <img src={Delete} alt="delete" />
                                    </Button>
                                </td>
                            </tr>
                        )) :
                            <tr>
                                <td colSpan={5} className='pt-4'>
                                    <h5>
                                        <b>{t("No groups found. Please add user groups and assign members")}.</b>
                                    </h5>
                                    <span>
                                        <b>{t("Note")}</b>:&nbsp;{t("Groups helps you organise and manage teams easily")}.
                                    </span>
                                </td>
                            </tr>
                    }
                </tbody>
            </Table >
        </Row>

    )
}

export default inject('commonStore', 'userGroupStore', 'userStore')(withLocalization(withRemoveDialog(ManageGroupsWidget)));

